import { AdminProfessionals } from '@platform/client';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, ModalCallToAction } from '@platform/components';

type InactiveModalStage = 'confirmation' | 'submitting' | 'error' | 'success';

export const MarkInactiveModal = (props: {
  userId: number;
  onClose: () => void;
  isOpen: boolean;
  userCurrentCohort?: string | null;
}) => {
  const [modalStage, setModalStage] = useState<InactiveModalStage>('confirmation');
  const handleClose = () => {
    setModalStage('confirmation');
    props.onClose();
  };
  const queryClient = useQueryClient();
  const inactivateUserMutation = AdminProfessionals.useInactivateUser({
    mutation: {
      onMutate: () => {
        setModalStage('submitting');
      },
      onSuccess: () => {
        setModalStage('success');
        queryClient.invalidateQueries({
          queryKey: [...AdminProfessionals.getGetProfessionalReviewDataQueryKey(props.userId)],
        });
      },
      onError: () => {
        setModalStage('error');
      },
    },
  });
  const primaryCTA: ModalCallToAction = {
    text: modalStage === 'confirmation' ? 'Mark inactive' : 'Close',
    buttonVariant: 'secondary-gray',
    callback: () => {
      if (modalStage === 'confirmation') {
        inactivateUserMutation.mutate({ userId: props.userId });
      } else {
        handleClose();
      }
    },
  };
  const secondaryCTA: ModalCallToAction | undefined = {
    text: 'Cancel',
    callback: props.onClose,
  };
  return (
    <Modal
      title='Mark inactive'
      variant='dialog'
      isOpen={props.isOpen}
      headerDivision='divider'
      onClose={handleClose}
      primaryCTA={primaryCTA}
      secondaryCTA={modalStage === 'confirmation' ? secondaryCTA : undefined}
      ignoreExtraPadding
    >
      <div className='w-full'>
        {modalStage === 'confirmation' && (
          <ConfirmInactivateUser userCurrentCohort={props.userCurrentCohort} />
        )}
        {modalStage === 'error' && <ErrorContent />}
        {modalStage === 'success' && <SuccessInactiveContent />}
      </div>
    </Modal>
  );
};

const ConfirmInactivateUser = ({ userCurrentCohort }: { userCurrentCohort?: string | null }) => {
  return (
    <div>
      <p className='text-gray-450'>
        This drops the professional
        {userCurrentCohort ? ` from the ${userCurrentCohort} cohort` : ''}.
      </p>
      <ul className='list-disc list-outside my-6 ml-4'>
        <li>
          The professional is hidden from search results and is ineligible for new matches or
          introductions.
        </li>
        <li>Pending introductions and discoveries will be rejected/withdrawn.</li>
      </ul>
      <p className='text-gray-450'>Remains eligible for future cohorts.</p>
    </div>
  );
};

const SuccessInactiveContent = () => {
  return <p>The user has been successfully inactivated.</p>;
};

const ErrorContent = () => {
  return <p>Sorry, there was an error processing your request. Please try again.</p>;
};
